import { forwardRef, type ReactNode, type SVGProps } from "react";

/**
 * Zettl navigation icons — one consistent monoline set (24px grid, 1.75 stroke,
 * round joins) drawn for the sidebar instead of generic library glyphs.
 * Size them with className like Lucide icons (`size-5`); color follows
 * `currentColor`.
 */
type IconProps = SVGProps<SVGSVGElement>;

function createIcon(displayName: string, paths: ReactNode) {
  const Icon = forwardRef<SVGSVGElement, IconProps>((props, ref) => (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {paths}
    </svg>
  ));
  Icon.displayName = displayName;
  return Icon;
}

/** Párování — two overlapping circles: a payment and its document. */
export const MatchIcon = createIcon(
  "MatchIcon",
  <>
    <circle cx="9" cy="12" r="6" />
    <circle cx="15" cy="12" r="6" />
  </>
);

/** Transakce — money in and out. */
export const TransactionsIcon = createIcon(
  "TransactionsIcon",
  <path d="M5 8h13M14 4l4 4-4 4M19 16H6M10 12l-4 4 4 4" />
);

/** Chybějící doklady — a sheet with a gap where the document should be. */
export const MissingDocIcon = createIcon(
  "MissingDocIcon",
  <>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9.5 14h5" />
  </>
);

/** Účtenky — a till receipt with a torn edge. */
export const ReceiptIcon = createIcon(
  "ReceiptIcon",
  <>
    <path d="M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5z" />
    <path d="M9 8h6M9 12h6M9 16h3" />
  </>
);

/** Faktury — a document with a folded corner. */
export const InvoiceIcon = createIcon(
  "InvoiceIcon",
  <>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5M9 13h6M9 17h4" />
  </>
);

export const ClientIcon = createIcon(
  "ClientIcon",
  <>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </>
);

/** Šablony — stacked sheets. */
export const TemplateIcon = createIcon(
  "TemplateIcon",
  <>
    <rect x="8" y="3" width="12" height="14" rx="2" />
    <path d="M16 21H6a2 2 0 0 1-2-2V7" />
  </>
);

export const AnalyticsIcon = createIcon(
  "AnalyticsIcon",
  <path d="M4 20h16M5 15l4.5-4.5 3.5 3.5L19 7M15 7h4v4" />
);

/** Rozpočet — a gauge: how much of the limit is used. */
export const BudgetIcon = createIcon(
  "BudgetIcon",
  <>
    <path d="M3.5 17a8.5 8.5 0 0 1 17 0" />
    <path d="M12 17l3.5-4.5" />
    <circle cx="12" cy="17" r="1.2" />
  </>
);

/** Přehled (personal) — a month calendar. */
export const OverviewIcon = createIcon(
  "OverviewIcon",
  <>
    <rect x="3.5" y="4.5" width="17" height="16" rx="2.5" />
    <path d="M3.5 9.5h17M8 3v3M16 3v3" />
  </>
);

export const SettingsIcon = createIcon(
  "SettingsIcon",
  <>
    <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
    <circle cx="15" cy="7" r="2" />
    <circle cx="9" cy="17" r="2" />
  </>
);

export const FeedbackIcon = createIcon(
  "FeedbackIcon",
  <path d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-7l-4 3.5V17H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" />
);

export const AdminIcon = createIcon(
  "AdminIcon",
  <>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M7.5 9.5l3 2.5-3 2.5M13 15h3.5" />
  </>
);

export const SignOutIcon = createIcon(
  "SignOutIcon",
  <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4M15 8l4 4-4 4M19 12H9" />
);

export const PanelIcon = createIcon(
  "PanelIcon",
  <>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M9.5 4v16" />
  </>
);

/** Workspace switcher affordance. */
export const ChevronsUpDownIcon = createIcon(
  "ChevronsUpDownIcon",
  <path d="M8 9.5l4-4 4 4M8 14.5l4 4 4-4" />
);
